import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import config from "../../api/config";
import { getHeaders, getTenantId } from "../../api/apiHelpers";
import { selectUser } from "../../store/slices/authSlice";
import ChatWidget from "./ChatWidget";
import AnonymousAIChat from "./AnonymousAIChat";

/**
 * Decides which floating chat surface to render based on auth state:
 * - Authenticated user → <ChatWidget /> (existing rich messaging UX)
 * - Non-authenticated visitor + tenant has opted in → <AnonymousAIChat />
 *   (single-purpose lead-capture chat, no card grid, no contact-support
 *   tab — just a real customer-service conversation)
 * - Non-authenticated visitor + tenant has NOT opted in → render nothing
 *
 * Both surfaces sit at bottom-right; rendering only one of them at a
 * time guarantees no button collision.
 *
 * Auth detection: this app's login flow uses a custom JWT (POST
 * /auth/login → localStorage.authToken + redux state.auth.userDetails),
 * NOT Supabase auth. AuthContext.user only reflects supabase.auth
 * session state, which stays null after a custom-JWT login. We watch
 * the Redux user slice (the authoritative source) plus localStorage
 * for storage-event-triggered cross-tab updates.
 */
const FloatingChatGate: React.FC = () => {
  const reduxUser = useSelector(selectUser);

  // Some flows (e.g. browser refresh restoring auth from localStorage
  // before Redux is rehydrated, or external scripts dispatching a
  // login) won't have populated reduxUser yet — watch authToken too.
  const [hasAuthToken, setHasAuthToken] = useState<boolean>(
    () => !!(typeof window !== "undefined" && localStorage.getItem("authToken"))
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "authToken" || e.key === null) {
        setHasAuthToken(!!localStorage.getItem("authToken"));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Re-check on every reduxUser change too — Login.tsx sets the token
  // and dispatches setUser back-to-back, so watching reduxUser keeps
  // the check current within the same tab even though `storage`
  // events don't fire for the writing tab.
  useEffect(() => {
    setHasAuthToken(!!localStorage.getItem("authToken"));
  }, [reduxUser]);

  const isAuthenticated = !!reduxUser || hasAuthToken;

  // If an anonymous chat is currently in progress (the visitor opened
  // it before signing in, or signed in via the inline form mid-chat),
  // keep AnonymousAIChat mounted regardless of auth state. This is
  // what preserves conversation continuity across the auth boundary.
  // Cleared by AnonymousAIChat when the visitor closes the panel.
  const [anonChatActive, setAnonChatActive] = useState<boolean>(
    () => !!(typeof window !== "undefined" && sessionStorage.getItem("anon_ai_active_session"))
  );
  useEffect(() => {
    const onStorage = () => {
      setAnonChatActive(!!sessionStorage.getItem("anon_ai_active_session"));
    };
    window.addEventListener("storage", onStorage);
    // Also poll once a second while authenticated — sessionStorage
    // events don't fire in the writing tab, and AnonymousAIChat sets
    // the flag in the same tab. Cheap.
    const interval = setInterval(onStorage, 1000);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, []);

  const [anonEnabled, setAnonEnabled] = useState<boolean | null>(null);
  const [tenantBranding, setTenantBranding] = useState<{
    primary_color?: string;
    business_name?: string;
    ai_chat_disclosure_text?: string | null;
  }>({});

  // Only fetch the public flag for non-authenticated visitors. Authed
  // users get the existing widget regardless.
  useEffect(() => {
    if (isAuthenticated) {
      setAnonEnabled(false);
      return;
    }
    const tenantId = getTenantId();
    if (!tenantId) {
      setAnonEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Public endpoint — no auth header. business_name + primary_color
        // are returned alongside ai_chat_anonymous_enabled.
        const resp = await axios.get(`${config.BASE_URL}/public/contact`, {
          headers: getHeaders(false),
        });
        if (cancelled) return;
        const data = resp.data?.data || {};
        setAnonEnabled(!!data.ai_chat_anonymous_enabled);
        setTenantBranding({
          primary_color: data.primary_color,
          business_name: data.business_name,
          ai_chat_disclosure_text: data.ai_chat_disclosure_text,
        });
      } catch {
        if (!cancelled) setAnonEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Anon-chat session keys are intentionally NOT cleared on auth —
  // mid-chat sign-in needs them to stay so the conversation can
  // continue. They get cleared only when the visitor closes the
  // anon chat panel (handled inside AnonymousAIChat).

  // If an anon chat is currently active (open or in-progress with a
  // pending member sign-in), keep AnonymousAIChat regardless of
  // auth state — preserves conversation continuity. Otherwise the
  // normal auth-based decision applies.
  if (anonChatActive) {
    return (
      <AnonymousAIChat
        businessName={tenantBranding.business_name}
        disclosureText={tenantBranding.ai_chat_disclosure_text}
      />
    );
  }
  if (isAuthenticated) {
    return <ChatWidget />;
  }
  if (anonEnabled) {
    return (
      <AnonymousAIChat
        businessName={tenantBranding.business_name}
        disclosureText={tenantBranding.ai_chat_disclosure_text}
      />
    );
  }
  return null;
};

export default FloatingChatGate;
